/* ============================================================== */
/*                                                                */
/*   consultModelV4  ·  domain ของหน้า Multidisciplinary (HIS V4)    */
/*                                                                */
/*   ไฟล์ pure: ไม่มี React ไม่มี issara-sdk                             */
/*   map payload API <-> domain + ประกอบ payload ที่ส่งกลับ              */
/*                                                                */
/*   API (reference จาก HIS V3: src/HIS/Interface/PTMI.ts:509-660,       */
/*   react-lib/apps/PTM/CardMultiDisciplinaryView.tsx,                       */
/*   MultiDisciplinaryEditor.tsx)                                        */
/*     GET    /apis/PTM/multidisciplinary-item/  ?encounter&start_date   */
/*            &end_date&practitioner_role&page                             */
/*     POST   /apis/PTM/multidisciplinary-item/                            */
/*     PUT    /apis/PTM/multidisciplinary-item/{pk}/                       */
/*     DELETE /apis/PTM/multidisciplinary-item/{pk}/                       */
/*     GET    /apis/PTM/multidisciplinary-item-log/?item={id}              */
/*     GET    /apis/PTM/multidisciplinary-record-latest/?encounter={id}    */
/*     GET    /apis/core/choices/?model=users.Role&field=type&name_as_id   */
/*          (ตัวเลือก "วิชาชีพ" สำหรับกรอง — V3 ใช้ getChoices แบบนี้)     */
/*                                                                */
/* ============================================================== */

import moment from "moment";

import { formatDateToStringBe } from "react-lib/utils/dateUtils";

import {
  asBoolean,
  asDict,
  asNumber,
  asText,
  decodeHtml,
  itemsOf,
  type Dict,
} from "./examModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE                                              */
/* ============================================================== */

/** label ของ "วิชาชีพ" ตามแบบหน้า (PatientNursing.dc.html sec-md) */
export const CONSULT_DISCIPLINES = [
  "แพทย์",
  "พยาบาล",
  "เภสัชกร",
  "นักกายภาพบำบัด",
  "นักโภชนาการ",
  "อื่น ๆ",
] as const;

export type ConsultDiscipline = (typeof CONSULT_DISCIPLINES)[number];

export type ConsultIds = {
  encounterId?: number;
  emrId?: number;
  patientId?: number;
};

/**บันทึกของทีมสหสาขา 1 รายการ (MultidisciplinaryItemSerializer_apps_PTM) */
export type ConsultNote = {
  id: number;
  /** วันที่เริ่ม (display) */
  date: string;
  /** ค่าดิบที่ API ส่งมา ใช้ตอน PUT เพื่อไม่เปลี่ยนรูปแบบที่ backend ให้ */
  dateRaw: string;
  endDate: string;
  /** Problem = ช่อง "บันทึก" ของแบบหน้า (required ใน V3) */
  problem: string;
  plan: string;
  goal: string;
  active: boolean;
  /** user id ของผู้บันทึก (serializer: practitioner) */
  practitioner: number | null;
  practitionerName: string;
  /** ชื่อวิชาชีพที่ display (resolve จาก role / ชื่อPrefix) */
  discipline: string;
  /** id ของ role ที่ backend ผูก (ใช้ตอนกรอง) */
  practitionerRole: number | null;
  recordId: number | null;
  created: string;
  edited: string;
  editUser: string;
};

export type ConsultLog = {
  id: number | null;
  action: string;
  user: string;
  datetime: string;
  changes: string;
};

export type ConsultFilter = {
  startDate: string;
  endDate: string;
  /** "" = ทั้งหมด */
  practitionerRole: string | number;
};

export const ConsultFilterInitial: ConsultFilter = {
  startDate: "",
  endDate: "",
  practitionerRole: "",
};

export type ConsultDraft = {
  date: string;
  endDate: string;
  problem: string;
  plan: string;
  goal: string;
  /**
   * credentials ของ "ผู้บันทึก" — backend ของ Multidisciplinary ใช้ยืนยันก่อนรับเรื่อง
   * HIS V3 มีช่อง Username กับ Password (บังคับทั้งสอง) ใน editor (`apps/PTM/MultiDisciplinaryEditor.tsx:58-59,97-98,220-252`)
   * และตอนลบต้องมี modal "ระบุ Username และ Password เพื่อลบ" (`CardMultiDisciplinaryView.tsx:625-632`)
   */
  username: string;
  password: string;
};

export const ConsultDraftInitial: ConsultDraft = {
  date: "",
  endDate: "",
  problem: "",
  plan: "",
  goal: "",
  username: "",
  password: "",
};

export type ConsultRoleOption = { value: string | number; label: string };

/** slice ของ sequence เก็บเฉพาะข้อมูล (loading/error อยู่ state กลาง) */
export type ConsultV4Slice = {
  sequenceIndex: string;
  ids: ConsultIds;
  notes: ConsultNote[];
  logs: ConsultLog[];
  /** logs ที่ bind กับ item id (LogList คืนทั้ง encounter) */
  logsByItem: Record<string, ConsultLog[]>;
  roleOptions: ConsultRoleOption[];
  filter: ConsultFilter;
  total: number;
  /** ข้อความสรุปผลเพิ่ม/แก้ไขบันทึกทีม (issue 86642 ข้อ 4 / 86644 ข้อ 5) */
  savedMessage: string;
};

export const emptyConsultV4Slice = (): ConsultV4Slice => ({
  sequenceIndex: "",
  ids: {},
  notes: [],
  logs: [],
  logsByItem: {},
  roleOptions: [],
  filter: { ...ConsultFilterInitial },
  total: 0,
  savedMessage: "",
});

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

/**
 * state อาจถูก RunSequence ล้างเหลือแค่ { sequenceIndex: "START" } ก่อน handler ทำงาน
 * (MainHISInterface.ts:700-712) จึง normalize ก่อนอ่านทุกที่
 */
export function normalizeConsultV4Slice(slice: Partial<ConsultV4Slice> | null | undefined): ConsultV4Slice {
  const base = emptyConsultV4Slice();
  if (slice === null || slice === undefined) return base;

  return {
    sequenceIndex: asText(slice.sequenceIndex) || base.sequenceIndex,
    ids: { ...base.ids, ...asDict(slice.ids) },
    notes: Array.isArray(slice.notes) ? slice.notes : base.notes,
    logs: Array.isArray(slice.logs) ? slice.logs : base.logs,
    logsByItem:
      slice.logsByItem !== null && typeof slice.logsByItem === "object" ? slice.logsByItem : base.logsByItem,
    roleOptions: Array.isArray(slice.roleOptions) ? slice.roleOptions : base.roleOptions,
    filter: { ...base.filter, ...asDict(slice.filter) },
    total: typeof slice.total === "number" ? slice.total : base.total,
    savedMessage: typeof slice.savedMessage === "string" ? slice.savedMessage : base.savedMessage,
  };
}

/* ============================================================== */
/*   SECTION 2 · วิชาชีพ (discipline)                              */
/* ============================================================== */

/**
 * backend ไม่ได้ส่ง "สาขา" ตรง ๆ ใน MultidisciplinaryItemSerializer
 * มีแค่ practitioner (user id) + practitioner_name (+ role ที่กรองด้วย practitioner_role)
 * จึง resolve จาก: field role -> roleOptions mapping -> prefix ของชื่อ (พญ./พ.น./ผู้ช่วย…)
 */
const ROLE_LABEL_ALIASES: { [key: string]: string } = {
  doctor: "แพทย์",
  physician: "แพทย์",
  แพทย์: "แพทย์",
  nurse: "พยาบาล",
  พยาบาล: "พยาบาล",
  pharmacist: "เภสัชกร",
  เภสัชกร: "เภสัชกร",
  "physical therapist": "นักกายภาพบำบัด",
  physical_therapist: "นักกายภาพบำบัด",
  นักกายภาพบำบัด: "นักกายภาพบำบัด",
  dietitian: "นักโภชนาการ",
  nutritionist: "นักโภชนาการ",
  นักโภชนาการ: "นักโภชนาการ",
};

/**
 * ชื่อนำหน้า (prefix) ที่ HIS ชอบใช้ในตัวชื่อผู้บันทึก — แม่นยำที่สุด
 * (backend ไม่มี field label ของ role ให้ V4 จึงต้อง resolve จาก practitioner_name)
 */
const NAME_PREFIX_HINTS: Array<[RegExp, string]> = [
  [/^(พญ\.?|นพ\.?|น\.?พ\.?|นายแพทย์|แพทย์หญิง|Dr\.?)/i, "แพทย์"],
  [/^(ผช\.?|ผู้ช่วยพยาบาล)/, "พยาบาล"],
  [/^(พ\.?ย\.?|พยาบาล)/, "พยาบาล"],
  [/^(ภ\.?พ\.?|เภสัช)/, "เภสัชกร"],
  [/^(นัก)?กายภาพ/, "นักกายภาพบำบัด"],
  [/^(นัก)?โภชน/, "นักโภชนาการ"],
];

/**
 * คำเรียกหน้าที่งานที่อยู่กลางชื่อ เช่น "นาง สมหญิง พยาบาล"
 * ลำดับต้องเข้มงวดกว่า prefix:ผู้ช่วยพยาบาล ต้องอยู่ก่อน พยาบาล และ แพทย์ ต้องอยู่ท้ายสุด
 */
const NAME_WORD_HINTS: Array<[RegExp, string]> = [
  [/ผู้ช่วยพยาบาล/, "พยาบาล"],
  [/พยาบาล/, "พยาบาล"],
  [/เภสัช/, "เภสัชกร"],
  [/กายภาพ/, "นักกายภาพบำบัด"],
  [/โภชน/, "นักโภชนาการ"],
  [/แพทย์หญิง|นายแพทย์|แพทย์/, "แพทย์"],
];

export const disciplineLabelOf = (raw: unknown): string | null => {
  const text = asText(raw).trim();
  if (text === "") return null;

  const alias = ROLE_LABEL_ALIASES[text.toLowerCase()];
  if (alias !== undefined) return alias;

  if ((CONSULT_DISCIPLINES as readonly string[]).includes(text)) return text;
  return text;
};

export const disciplineFromName = (name: unknown): string | null => {
  const text = asText(name).trim();
  if (text === "") return null;

  const prefix = NAME_PREFIX_HINTS.find(([pattern]) => pattern.test(text));
  if (prefix !== undefined) return prefix[1];

  const word = NAME_WORD_HINTS.find(([pattern]) => pattern.test(text));
  return word === undefined ? null : word[1];
};

/** resolve วิชาชีพของ 1 แถวจาก API (เรียงจากชัดเจน -> เดาจากชื่อ) */
export const consultDisciplineOf = (
  row: Dict,
  roleOptions: ConsultRoleOption[],
): string => {
  const direct =
    disciplineLabelOf(row.practitioner_role_name) ??
    disciplineLabelOf(row.role_name) ??
    disciplineLabelOf(row.practitioner_display) ??
    disciplineLabelOf(row.discipline) ??
    disciplineLabelOf(row.practitioner_type) ??
    disciplineLabelOf(row.practitioner_role_display);

  if (direct !== null) return direct;

  const roleId = asNumber(row.practitioner_role) ?? asNumber(row.role);
  if (roleId !== undefined) {
    const option = roleOptions.find((item) => asNumber(item.value) === roleId);
    if (option !== undefined) return option.label;
  }

  const fromName = disciplineFromName(row.practitioner_name) ?? disciplineFromName(row.practitioner);
  if (fromName !== null) return fromName;

  return "ทีมสหสาขา";
};

/* ============================================================== */
/*   SECTION 3 · MAP API -> DOMAIN                                 */
/* ============================================================== */

const displayNameOf = (row: Dict): string =>
  asText(row.practitioner_name) ||
  asText(row.practitioner_display_name) ||
  asText(row.user_name) ||
  (row.practitioner !== undefined && row.practitioner !== null ? `ผู้ใช้ ${asText(row.practitioner)}` : "");

const editUserText = (row: Dict): string => {
  const value = row.edit_user;
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return displayNameOf(value as Dict) || asText((value as Dict).username);
  return asText(value);
};

/**
 * GET /apis/PTM/multidisciplinary-item/
 * list ที่ generate มาคืน { items: [...], total } — ต้องอ่านด้วย itemsOf (กฎของ repo)
 */
export function consultNotesFromApi(payload: unknown, roleOptions: ConsultRoleOption[] = []): ConsultNote[] {
  return itemsOf(payload).map((row) => {
    const name = displayNameOf(row);
    return {
      id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
      date: asText(row.formatted_date) || asText(row.date_be) || asText(row.date),
      dateRaw: asText(row.date),
      endDate: asText(row.formatted_end_date) || asText(row.end_date),
      problem: decodeHtml(row.problem),
      plan: decodeHtml(row.plan),
      goal: decodeHtml(row.goal),
      active: row.active === undefined ? true : asBoolean(row.active),
      practitioner: asNumber(row.practitioner) ?? null,
      practitionerName: name,
      discipline: consultDisciplineOf(row, roleOptions),
      practitionerRole: asNumber(row.practitioner_role) ?? asNumber(row.role) ?? null,
      recordId: asNumber(row.multidisciplinary_record) ?? null,
      created: asText(row.created),
      edited: asText(row.edited),
      editUser: editUserText(row),
    };
  });
}

/** เรียงรายการใหม่สุดก่อน (issue 86643 ข้อ 4) ด้วย created (ISO) แล้ว fallback id */
export function sortConsultNotesNewestFirst(notes: ConsultNote[]): ConsultNote[] {
  return notes.slice().sort((a, b) => {
    const left = asText(a.created) || asText(a.date);
    const right = asText(b.created) || asText(b.date);
    if (left === right) return b.id - a.id;
    return left < right ? 1 : -1;
  });
}

/**
 * backend ตอบ edit_user_name เป็น "ชื่อ(รหัส_emp)" (MultidisciplinaryItemLogSerializer:
 * his/apps/PTM/serializers.py:1257-1271) -> แยกเป็นชื่อ (บรรทัดบน) + รหักรายการ (บรรทัดล่าง)
 * ตาม issue 86645 ข้อ 2
 */
export function consultLogUserParts(user: string): { name: string; code: string } {
  const text = asText(user).trim();
  const match = text.match(/^(.*)\(([^)]*)\)$/);
  if (match === null) return { name: text === "" ? "-" : text, code: "" };
  return { name: match[1].trim(), code: match[2].trim() };
}

/** badge สถานะใน popup Log: CREATE = เขียว, EDIT = เหลือง (issue 86645 ข้อ 3) */
export const consultLogBadgeClassOf = (action: string): string =>
  action.toUpperCase() === "CREATE" ? "hv4-log-badge hv4-log-badge-create" : "hv4-log-badge hv4-log-badge-edit";

/** HTML สำหรับ Print บันทึกทีมสหสาขาที่เลือก (issue 86647 ข้อ 2) */
export function consultPrintHtml(
  notes: ConsultNote[],
  args: { encounterNumber?: string } = {},
): string {
  const rows = notes
    .map(
      (note) =>
        "<tr>" +
        `<td>${escapeConsult(note.date)}</td>` +
        `<td>${escapeConsult(note.problem)}</td>` +
        `<td>${escapeConsult(note.plan)}</td>` +
        `<td>${escapeConsult(note.goal)}</td>` +
        `<td>${escapeConsult(`${note.practitionerName}${note.discipline ? ` (${note.discipline})` : ""}`)}</td>` +
        `<td>${escapeConsult(note.endDate === "" ? "-" : note.endDate)}</td>` +
        "</tr>",
    )
    .join("");

  return (
    "<table>" +
    "<thead><tr><th>Start Date</th><th>Problem</th><th>Plan</th><th>Goal</th><th>Practitioner</th><th>End Date</th></tr></thead>" +
    `<tbody>${rows}</tbody>` +
    "</table>"
  );
}

const escapeConsult = (text: unknown): string =>
  `${text ?? ""}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** GET /apis/PTM/multidisciplinary-item-log/?item=  (MultidisciplinaryItemLogSerializer) */
export function consultLogsFromApi(payload: unknown): ConsultLog[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? null,
    action: asText(row.action).toUpperCase(),
    user: asText(row.edit_user_name) || asText(row.user_name) || "-",
    datetime: asText(row.datetime) || asText(row.created),
    changes: consultLogChangesText(row.changes),
  }));
}

/** changes เป็น dict/array/string ก็ได้ (serializer ประกาศเป็น any) */
export function consultLogChangesText(changes: unknown): string {
  if (changes === null || changes === undefined) return "";
  if (typeof changes === "string") return changes;
  if (Array.isArray(changes)) return changes.map((item) => asText(item)).filter((text) => text !== "").join(", ");

  return Object.entries(asDict(changes))
    .map(([key, value]) => {
      const isStructured = Array.isArray(value) || (typeof value === "object" && value !== null);
      const text = isStructured ? JSON.stringify(value) : asText(value);
      return `${key}: ${text}`;
    })
    .join(" · ");
}

/** GET /apis/PTM/multidisciplinary-record-latest/?encounter= */
export function consultRecordIdFromApi(payload: unknown): number | null {
  const data = asDict(payload);
  return asNumber(data.id) ?? asNumber(data.pk) ?? null;
}

/**
 * GET /apis/core/choices/?model=users.Role&field=type&name_as_id=true
 * V3 (CardMultiDisciplinaryView.tsx:180-200) เติมตัวเลือก "ทั้งหมด" ที่ index 0
 */
export function consultRoleOptionsFromApi(payload: unknown): ConsultRoleOption[] {
  const rows = itemsOf(payload).length > 0 ? itemsOf(payload) : asListLike(payload);
  const allOption: ConsultRoleOption = { value: "", label: "ทั้งหมด" };

  if (rows.length === 0) return [allOption];

  const options = rows
    .map((row) => {
      const value = asNumber(row.id) ?? asNumber(row.pk) ?? (typeof row.value === "string" ? asText(row.value) : asNumber(row.value));
      const label =
        disciplineLabelOf(row.label) ??
        disciplineLabelOf(row.text) ??
        disciplineLabelOf(row.value) ??
        disciplineLabelOf(row.name) ??
        asText(row.label);

      if (value === undefined || label === "") return null;
      return { value, label } as ConsultRoleOption;
    })
    .filter((option): option is ConsultRoleOption => option !== null);

  return [allOption].concat(options);
}

/** choices endpoint อาจตอบ array ตรง ๆ หรือ { choices|results|data: [...] } */
const asListLike = (payload: unknown): Dict[] => {
  if (Array.isArray(payload)) return itemsOf(payload);

  const data = asDict(payload);
  for (const key of ["choices", "results", "data", "options"]) {
    if (Array.isArray(data[key])) return itemsOf(data[key]);
  }
  return [];
};

/* ============================================================== */
/*   SECTION 4 · PARAM / PAYLOAD ที่ส่งให้ API                      */
/* ============================================================== */

/** วันที่แบบที่ API ชุดนี้รับ (V3 ใช้ formatDateToStringBe = DD/MM/YYYY พ.ศ.) */
export const consultDateOfNow = (): string => formatDateToStringBe(moment());

/** params ของ list (V3: encounter / start_date / end_date / practitioner_role) */
export function consultListParams(args: {
  encounterId?: number;
  filter?: Partial<ConsultFilter>;
  page?: number;
}): Dict {
  const params: Dict = {};
  if (args.encounterId !== undefined) params.encounter = args.encounterId;

  const start = asText(args.filter?.startDate).trim();
  const end = asText(args.filter?.endDate).trim();
  const role = args.filter?.practitionerRole;

  if (start !== "") params.start_date = start;
  if (end !== "") params.end_date = end;
  if (role !== undefined && `${role}` !== "" && role !== 0) params.practitioner_role = role;
  if (args.page !== undefined) params.page = args.page;

  return params;
}

/**
 * body ของ POST/PUT (MultidisciplinaryItemSerializer)
 * - problem required ตาม V3 editor
 * - date required (Start Date)
 * - practitioner/active/record ส่งเมื่อมีค่าเท่านั้น (serializer อ่านอย่างย่อ)
 */
export function buildConsultPayload(args: {
  encounterId?: number;
  draft: ConsultDraft;
  practitionerId?: number | null;
  recordId?: number | null;
  active?: boolean;
  /** credentials ของผู้บันทึก (backend ต้องการ — ดู header ของ ConsultDraft) */
  username?: string;
  password?: string;
}): Dict {
  const draft = args.draft;

  const payload: Dict = {
    date: asText(draft.date),
    problem: asText(draft.problem).trim(),
    plan: asText(draft.plan).trim(),
    goal: asText(draft.goal).trim(),
  };

  /* V3 ส่ง username/password ทุกครั้งที่บันทึก (editor เก็บไว้เป็น state ของ form)
     เอาจาก draft เป็น default และให้ caller override ได้ */
  const username = args.username === undefined ? draft.username : args.username;
  const password = args.password === undefined ? draft.password : args.password;
  if (username !== undefined) payload.username = asText(username).trim();
  if (password !== undefined) payload.password = asText(password);

  const end = asText(draft.endDate).trim();
  payload.end_date = end === "" ? null : end;

  if (args.encounterId !== undefined) payload.encounter = args.encounterId;
  if (args.practitionerId !== undefined && args.practitionerId !== null) payload.practitioner = args.practitionerId;
  if (args.recordId !== undefined && args.recordId !== null) payload.multidisciplinary_record = args.recordId;
  if (args.active !== undefined) payload.active = args.active;

  return payload;
}

/** body ของ PUT: ใช้ค่า dateRaw เดิมถ้าไม่ได้แก้ เพื่อให้รูปแบบ date คงเดิม */
export function buildConsultUpdatePayload(args: {
  encounterId?: number;
  note: ConsultNote;
  draft: ConsultDraft;
  username?: string;
  password?: string;
}): Dict {
  const date = asText(args.draft.date).trim() === "" ? asText(args.note.dateRaw) : asText(args.draft.date).trim();
  const end =
    asText(args.draft.endDate).trim() === "" ? asText(args.note.endDate) : asText(args.draft.endDate).trim();

  return {
    ...buildConsultPayload({
      encounterId: args.encounterId,
      draft: {
        date,
        endDate: end,
        problem: args.draft.problem,
        plan: args.draft.plan,
        goal: args.draft.goal,
        username: args.draft.username,
        password: args.draft.password,
      },
      recordId: args.note.recordId,
      practitionerId: args.note.practitioner,
      active: args.note.active,
      username: args.username ?? args.draft.username,
      password: args.password ?? args.draft.password,
    }),
    id: args.note.id,
  };
}

/** ช่องบังคับแบบเดียวกับ V3 editor: Start Date + Problem + Username + Password */
export function validateConsultDraft(draft: ConsultDraft): { ok: boolean; error: string | null } {
  if (asText(draft.problem).trim() === "") return { ok: false, error: "กรุณากรอก Problem/บันทึกก่อนบันทึก" };
  if (asText(draft.date).trim() === "") return { ok: false, error: "ยังไม่ระบุวันที่บันทึก (Start Date จำเป็น)" };
  if (asText(draft.username).trim() === "") return { ok: false, error: "ต้องระบุ Username ของผู้บันทึก" };
  if (asText(draft.password) === "") return { ok: false, error: "ต้องระบุ Password ของผู้บันทึก" };
  return { ok: true, error: null };
}

/** credentials สำหรับกรณีลบ (V3 ใช้ modalแยก — username + password เท่านั้น) */
export function buildConsultDeletePayload(args: { username: string; password: string }): Dict {
  return { username: asText(args.username).trim(), password: asText(args.password) };
}

export function validateConsultCredentials(args: { username: string; password: string }): {
  ok: boolean;
  error: string | null;
} {
  if (asText(args.username).trim() === "") return { ok: false, error: "ต้องระบุ Username" };
  if (asText(args.password) === "") return { ok: false, error: "ต้องระบุ Password" };
  return { ok: true, error: null };
}

/* ============================================================== */
/*   SECTION 5 · DISPLAY                                            */
/* ============================================================== */

/** text เดียวของแถว (Problem + Plan/Goal ต่อท้ายเมื่อมีค่า) */
export function consultNoteText(note: ConsultNote): string {
  const parts: string[] = [];
  if (note.problem !== "") parts.push(note.problem);
  if (note.plan !== "") parts.push(`Plan: ${note.plan}`);
  if (note.goal !== "") parts.push(`Goal: ${note.goal}`);
  return parts.join("\n");
}

export function consultDateRangeText(note: ConsultNote): string {
  const start = note.date;
  const end = note.endDate;
  if (start !== "" && end !== "") return `${start} – ${end}`;
  return start;
}

/** ผู้บันทึก: ชื่อ + เวลาที่สร้าง (แบบ "by · at" ในหน้า design) */
export function consultAuthorText(note: ConsultNote): string {
  const who = note.practitionerName !== "" ? note.practitionerName : "ไม่ระบุผู้บันทึก";
  const when = note.created !== "" ? note.created : note.date;
  return `${who} · ${when}`;
}

/** แถวที่มี id ใช้แก้ไข/ลบได้ (V3 ล็อกเมื่อ user ปัจจุบันไม่ใช่ผู้บันทึก) */
export const consultNoteEditable = (note: ConsultNote, currentUserId?: number): boolean => {
  if (note.id === 0) return false;
  if (currentUserId === undefined || note.practitioner === null) return true;
  return note.practitioner === currentUserId;
};

/** filter ที่ UI ใช้จริง (role dropdown value -> params) */
export const consultRoleFilterOf = (
  roleOptions: ConsultRoleOption[],
  discipline: string,
): string | number => {
  const option = roleOptions.find((item) => item.label === discipline);
  return option === undefined ? "" : option.value;
};

export const consultDisciplineFilterOptions = (roleOptions: ConsultRoleOption[]): string[] => {
  const labels = roleOptions.filter((item) => `${item.value}` !== "").map((item) => item.label);
  const disciplines: string[] = [...CONSULT_DISCIPLINES];
  const merged: string[] = disciplines.filter((label) => labels.includes(label));
  const extra = labels.filter((label) => !disciplines.includes(label));
  return merged.concat(extra);
};
